import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VisuallyHidden
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { RichTextField } from '../../../../components/RichTextField'
import { SearchableSelect } from '../../../../components/Select'
import { useHasRole } from '../../../../helpers'
import {
  IBannerData,
  IBannerPageCardProps,
  IBannerRequest,
  Tags,
  tagsLabels
} from '../../../../interfaces/Website/Banners/IBanners'
import BannerPreview from './BannerPreview'
import { getQueryParamsManually, isPersistedBanner, normalizeRichText, withUrlScheme } from './helpers'
import SiteColorInput from './SiteColorInput'
import { DEFAULT_TEXT_COLORS, tagStyle } from './siteColors'

// Lado máximo do recorte, em px. Sem ele o recorte saía na resolução cheia e no formato
// original — um PNG grande re-encodado pelo canvas passava dos 4MB que a API aceita e o banner
// voltava com 422. Com o teto ele sai em JPEG, folgado para tela retina: 2x o desktop (1200) e
// 3x o mobile (413).
const DESKTOP_IMAGE_MAX_SIDE = 2400
const MOBILE_IMAGE_MAX_SIDE = 1240

// Os campos de cada aba de mídia. Um erro numa aba fechada não aparecia: quem preenchia só o
// mobile ficava na aba Mobile, e o "imagem é obrigatória" do desktop nunca chegava à tela.
const DESKTOP_MEDIA_FIELDS: (keyof IBannerRequest)[] = ['image', 'video_url']
const MOBILE_MEDIA_FIELDS: (keyof IBannerRequest)[] = ['image_mobile', 'video_url_mobile']
const DESKTOP_TAB = 0
const MOBILE_TAB = 1

/** Cada campo de mídia e o seu par na mesma aba. */
const MEDIA_PAIRS: Record<string, keyof IBannerRequest> = {
  image: 'video_url',
  video_url: 'image',
  image_mobile: 'video_url_mobile',
  video_url_mobile: 'image_mobile'
}

/**
 * O modo que mostra o campo recusado, ou `null` quando os dois estão (o "envie uma imagem ou um
 * vídeo", que aparece em qualquer modo) ou nenhum está.
 */
function mediaTypeWithError(
  errors: IBannerPageCardProps['errors'] | undefined,
  imageField: keyof IBannerRequest,
  videoField: keyof IBannerRequest
): 'image' | 'video' | null {
  const imageError = !!errors?.[imageField]?.length
  const videoError = !!errors?.[videoField]?.length

  if (imageError === videoError) return null
  return imageError ? 'image' : 'video'
}

/**
 * Pontinho vermelho no rótulo da aba que tem campo recusado. O ponto é só visual; o leitor de tela
 * ouve "com erro" junto do nome da aba.
 */
function TabErrorDot() {
  return (
    <>
      <Box as="span" display="inline-block" w={2} h={2} ml={2} borderRadius="full" bg="red.500" aria-hidden />
      <VisuallyHidden>(com erro)</VisuallyHidden>
    </>
  )
}

const Error = styled(Text)`
  color: red.500;
  font-size: 0.8rem;
`

type ITagColorField = keyof Pick<
  IBannerData,
  | 'tag_online_color'
  | 'tag_online_ao_vivo_color'
  | 'tag_presencial_color'
  | 'tag_online_text_color'
  | 'tag_online_ao_vivo_text_color'
  | 'tag_presencial_text_color'
>

const TAG_COLOR_FIELDS: { tag: Tags; label: string; background: ITagColorField; text: ITagColorField }[] = [
  { tag: 'online', label: 'online', background: 'tag_online_color', text: 'tag_online_text_color' },
  {
    tag: 'online ao vivo',
    label: 'online ao vivo',
    background: 'tag_online_ao_vivo_color',
    text: 'tag_online_ao_vivo_text_color'
  },
  { tag: 'presencial', label: 'presencial', background: 'tag_presencial_color', text: 'tag_presencial_text_color' }
]

function BannerPageCard({
  data,
  fonts,
  onChange,
  errors,
  changePosition,
  openDeleteDialog,
  handleSwitch,
  isSwitchSaving
}: IBannerPageCardProps) {
  const [banner, setBanner] = useState(data)
  const preview = useDisclosure()
  const hasRole = useHasRole()
  const permissions = {
    editThisBanner: isPersistedBanner(data.id) ? hasRole('edit_banner') : hasRole('add_banner'),
    deleteBanner: hasRole('delete_banner')
  }

  useEffect(() => {
    if (!banner) {
      setMediaType(data.video_url ? 'video' : 'image')
      setMediaTypeMobile(data.video_url_mobile ? 'video' : 'image')
    }
    setBanner(data)
  }, [data])

  useEffect(() => {
    onChange(banner)
  }, [banner])

  const [mediaType, setMediaType] = useState<string>(data.video_url ? 'video' : 'image')
  const [mediaTypeMobile, setMediaTypeMobile] = useState<string>(data.video_url_mobile ? 'video' : 'image')

  // Campos mexidos desde que o último save devolveu os erros. O erro de um campo some assim que
  // ele é corrigido, em vez de ficar vermelho até o próximo clique em "Salvar alterações" — e o
  // usuário vê o que ainda falta.
  const [editedSinceErrors, setEditedSinceErrors] = useState<Set<string>>(new Set())

  // A aba de mídia é controlada para abrir sozinha na que tem erro depois de um save recusado.
  const [mediaTab, setMediaTab] = useState(DESKTOP_TAB)

  useEffect(() => {
    setEditedSinceErrors(new Set())

    if (DESKTOP_MEDIA_FIELDS.some((field) => errors?.[field]?.length)) setMediaTab(DESKTOP_TAB)
    else if (MOBILE_MEDIA_FIELDS.some((field) => errors?.[field]?.length)) setMediaTab(MOBILE_TAB)

    // E no modo (imagem ou vídeo) do campo recusado: um vídeo inválido com o modo em "Imagem"
    // deixava a aba com erro sem nenhum campo vermelho à vista.
    const desktopMode = mediaTypeWithError(errors, 'image', 'video_url')
    if (desktopMode) setMediaType(desktopMode)
    const mobileMode = mediaTypeWithError(errors, 'image_mobile', 'video_url_mobile')
    if (mobileMode) setMediaTypeMobile(mobileMode)
  }, [errors])

  const fieldError = (field: keyof IBannerRequest): string[] | undefined =>
    editedSinceErrors.has(field) ? undefined : errors?.[field]

  const hasErrorIn = (fields: (keyof IBannerRequest)[]) => fields.some((field) => !!fieldError(field)?.length)

  const updateField = (field: string, value: string | string[] | File | null | boolean | number) => {
    const patch: Partial<Record<string, typeof value>> = { [field]: value }
    const pair = MEDIA_PAIRS[field]

    // Imagem e vídeo da mesma aba são um ou outro, como a API grava: com arquivo novo ela descarta
    // o vídeo, e com vídeo o arquivo novo venceria mesmo com o modo em "Vídeo". O formulário passa
    // a mostrar o que vai ser salvo — e um vídeo inválido esquecido no modo "Imagem" não trava mais
    // o save de um banner que já tem imagem.
    if (pair && value instanceof File) patch[pair] = ''
    if (pair && typeof value === 'string' && value && banner[pair as keyof IBannerData] instanceof File) {
      patch[pair] = null
    }

    setBanner((previous) => ({ ...previous, ...patch }))
    setEditedSinceErrors((previous) => {
      // A imagem e o vídeo dividem o "envie uma imagem ou um vídeo": resolver um resolve os dois.
      const fields = pair ? [field, pair] : [field]
      return fields.every((edited) => previous.has(edited)) ? previous : new Set([...Array.from(previous), ...fields])
    })
  }

  /** Ao sair de um campo de URL, completa o `https://` que a API exige e quase ninguém digita. */
  const completeUrl = (field: 'cta_link' | 'video_url' | 'video_url_mobile') => {
    const value = banner[field] ?? ''
    const completed = withUrlScheme(value)
    if (completed !== value) updateField(field, completed)
  }

  function isYouTubeUrl(url: string): boolean {
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be|youtube-nocookie\.com).*?$/
    return youtubeRegex.test(url)
  }

  function getYouTubeVideoId(url: string): string | null {
    const params = getQueryParamsManually(url)

    return params.v || null
  }

  function isVimeoUrl(url: string): boolean {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    return vimeoRegex.test(url)
  }

  function getVimeoVideoId(url: string): string | null {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    const match = url.match(vimeoRegex)
    // O ID do vídeo é capturado no primeiro grupo (match[1])
    return match && match[1] ? match[1] : null
  }

  return (
    <Flex
      bg={!banner.is_active ? 'gray.100' : 'white'}
      borderRadius="md"
      p={5}
      borderWidth="2px"
      borderColor="gray.200"
      gap={4}
      flexWrap="wrap"
      shadow="md"
      _hover={{
        shadow: 'lg',
        transitionDuration: '0.3s'
      }}
    >
      <Stack flex={2}>
        {/* O card inteiro é a alça do arrasto (`BannerPageSortableBanner`): sem parar o
            `pointerdown` aqui, selecionar um trecho com o mouse para deixá-lo em negrito
            arrastaria o banner. */}
        <Stack spacing={3} onPointerDown={(e) => e.stopPropagation()}>
          <RichTextField
            label="Título"
            toolbar="banner"
            fonts={fonts}
            minH="60px"
            value={banner.title ?? ''}
            onChange={(html) => updateField('title', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={fieldError('title')}
          />
          <RichTextField
            label="Subtítulo"
            toolbar="banner"
            fonts={fonts}
            minH="60px"
            value={banner.subtitle ?? ''}
            onChange={(html) => updateField('subtitle', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={fieldError('subtitle')}
          />
          <RichTextField
            label="Texto do CTA"
            toolbar="banner"
            fonts={fonts}
            minH="40px"
            value={banner.cta_text ?? ''}
            onChange={(html) => updateField('cta_text', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={fieldError('cta_text')}
            helperText="No site o texto do botão sai em maiúsculas."
          />
        </Stack>
        <Box>
          <FormField
            label="Link do CTA"
            htmlFor="ctaLink"
            error={fieldError('cta_link')}
            helperText="Endereço completo do link, como https://abra.com.br/cursos."
          >
            <Input
              id="ctaLink"
              size="md"
              bg="whiteAlpha"
              color="gray.900"
              borderWidth="1px"
              borderColor="gray.300"
              borderRadius="md"
              value={banner.cta_link}
              onChange={(e) => updateField('cta_link', e.target.value)}
              onBlur={() => completeUrl('cta_link')}
              disabled={!permissions.editThisBanner}
            />
          </FormField>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={3} py={2}>
          <HStack spacing={4}>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="titleColor"
                  stored={banner.title_color}
                  fallback={DEFAULT_TEXT_COLORS.title}
                  onChange={(value) => updateField('title_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="titleColor" fontSize="sm" m={0}>
                  Cor do Título
                </FormLabel>
              </FormControl>
              {fieldError('title_color') && <Error color="red.500">{fieldError('title_color')?.[0]}</Error>}
            </Box>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="subtitleColor"
                  stored={banner.subtitle_color}
                  fallback={DEFAULT_TEXT_COLORS.subtitle}
                  onChange={(value) => updateField('subtitle_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="subtitleColor" fontSize="sm" m={0}>
                  Cor do Subtítulo
                </FormLabel>
              </FormControl>
              {fieldError('subtitle_color') && <Error color="red.500">{fieldError('subtitle_color')?.[0]}</Error>}
            </Box>
          </HStack>
          <HStack spacing={4}>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="buttonTextColor"
                  stored={banner.button_text_color}
                  fallback={DEFAULT_TEXT_COLORS.buttonText}
                  onChange={(value) => updateField('button_text_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="buttonTextColor" fontSize="sm" m={0}>
                  Cor do Texto do Botão
                </FormLabel>
              </FormControl>
              {fieldError('button_text_color') && <Error color="red.500">{fieldError('button_text_color')?.[0]}</Error>}
            </Box>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="buttonColor"
                  stored={banner.button_color}
                  fallback={null}
                  onChange={(value) => updateField('button_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="buttonColor" fontSize="sm" m={0}>
                  Cor do Botão
                </FormLabel>
              </FormControl>
              {fieldError('button_color') && <Error color="red.500">{fieldError('button_color')?.[0]}</Error>}
            </Box>
          </HStack>
        </Box>
        <Box>
          <FormField label="Gradiente">
            <Box w="33%">
              <Flex align="center" gap={2}>
                <Switch
                  title={data.is_gradient_active ? 'Desativar Gradiente do Banner' : 'Ativar Gradiente do Banner'}
                  isChecked={!!data.is_gradient_active}
                  onChange={(e) => updateField('is_gradient_active', e.target.checked ? 1 : 0)}
                  isDisabled={!permissions.editThisBanner}
                />
                <SiteColorInput
                  id="gradientColor"
                  disabled={!data.is_gradient_active || !permissions.editThisBanner}
                  stored={banner.gradient_color}
                  fallback={null}
                  onChange={(value) => updateField('gradient_color', value)}
                />
                <FormLabel htmlFor="gradientColor" fontSize="sm" m={0}>
                  Cor do Gradiente
                </FormLabel>
              </Flex>
              {fieldError('gradient_color') && <Error color="red.500">{fieldError('gradient_color')?.[0]}</Error>}
            </Box>
          </FormField>
        </Box>
        <Box>
          <FormField label="Tags">
            <SearchableSelect
              isMulti
              value={banner.tags ?? []}
              options={[
                { label: tagsLabels.online, value: 'online' },
                { label: tagsLabels['online ao vivo'], value: 'online ao vivo' },
                { label: tagsLabels.presencial, value: 'presencial' }
              ]}
              onChange={(tags) => updateField('tags', tags)}
              placeholder="Selecione uma ou mais tags"
              entity="tag"
              feminine
              isDisabled={!permissions.editThisBanner}
            />
          </FormField>
          {fieldError('tags') && <Error color="red.500">{fieldError('tags')?.[0]}</Error>}
          <Stack pt={5} spacing={3}>
            {TAG_COLOR_FIELDS.map(({ tag, label, background, text }) => {
              const isDisabled = !banner.tags?.includes(tag) || !permissions.editThisBanner
              const colors = tagStyle(banner, tag)

              return (
                <HStack key={tag} spacing={4} align="start">
                  <FormField w="50%" error={fieldError(background)}>
                    <Flex align="center" gap={2}>
                      <SiteColorInput
                        disabled={isDisabled}
                        stored={banner[background]}
                        fallback={colors.background}
                        onChange={(value) => updateField(background, value)}
                      />
                      <FormLabel fontSize="sm" m={0}>
                        Cor da tag {label}
                      </FormLabel>
                    </Flex>
                  </FormField>
                  <FormField w="50%" error={fieldError(text)}>
                    <Flex align="center" gap={2}>
                      <SiteColorInput
                        disabled={isDisabled}
                        stored={banner[text]}
                        fallback={colors.color}
                        onChange={(value) => updateField(text, value)}
                      />
                      <FormLabel fontSize="sm" m={0}>
                        Cor do texto da tag {label}
                      </FormLabel>
                    </Flex>
                  </FormField>
                </HStack>
              )
            })}
          </Stack>
        </Box>
      </Stack>

      {/* image/video select */}
      <Stack flex={1} spacing={4}>
        <Box onPointerDown={(event) => event.stopPropagation()}>
          <ActionButton action="view" label="Pré-visualizar banner" showLabel onClick={preview.onOpen} />
        </Box>
        <Tabs isLazy index={mediaTab} onChange={setMediaTab}>
          <TabList>
            <Tab>
              Desktop
              {hasErrorIn(DESKTOP_MEDIA_FIELDS) && <TabErrorDot />}
            </Tab>
            <Tab>
              Mobile
              {hasErrorIn(MOBILE_MEDIA_FIELDS) && <TabErrorDot />}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Stack flex={1} spacing={4}>
                <RadioGroup
                  onChange={setMediaType}
                  value={mediaType}
                  colorScheme="gray"
                  isDisabled={!permissions.editThisBanner}
                >
                  <Stack direction="row" color="gray.700">
                    <Radio value="image">Imagem</Radio>
                    <Radio value="video">Vídeo</Radio>
                  </Stack>
                </RadioGroup>
                {mediaType === 'image' ? (
                  <>
                    <ImageCropInput
                      label="Imagem"
                      isRequired
                      value={banner.image ?? null}
                      onChange={(file) => updateField('image', file)}
                      aspectRatio={1200 / 420}
                      maxOutputSize={DESKTOP_IMAGE_MAX_SIDE}
                      suggestedSize="1200px x 420px"
                      previewAspectRatio="1200 / 420"
                      isDisabled={!permissions.editThisBanner}
                      error={fieldError('image')}
                    />
                  </>
                ) : (
                  <>
                    <FormField label="URL do vídeo" isRequired error={fieldError('video_url')}>
                      <Input
                        placeholder="Insira a URL do vídeo"
                        value={banner.video_url}
                        onChange={(e) => updateField('video_url', e.target.value)}
                        onBlur={() => completeUrl('video_url')}
                        type="url"
                        color="gray.900"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="md"
                        px={4}
                        py={2}
                        _hover={{
                          borderColor: 'gray.500',
                          '&::placeholder': {
                            color: 'gray.700'
                          }
                        }}
                        _focus={{
                          borderColor: 'gray.600',
                          boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
                        }}
                        _placeholder={{ color: 'gray.500' }}
                        disabled={!permissions.editThisBanner}
                      />
                    </FormField>

                    {banner.video_url && (
                      <>
                        {isVimeoUrl(banner.video_url) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              src={`https://player.vimeo.com/video/${getVimeoVideoId(banner.video_url)}`}
                              title="Vídeo Preview"
                              width="100%"
                              height="200"
                              allow="autoplay; fullscreen"
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px'
                              }}
                            />
                          </Box>
                        )}
                        {isYouTubeUrl(banner.video_url) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              width="100%"
                              height="200"
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(banner.video_url)}`}
                              title="YouTube video player"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
            <TabPanel>
              <Stack flex={1} spacing={4}>
                <RadioGroup
                  onChange={setMediaTypeMobile}
                  value={mediaTypeMobile}
                  colorScheme="gray"
                  isDisabled={!permissions.editThisBanner}
                >
                  <Stack direction="row" color="gray.700">
                    <Radio value="image">Imagem</Radio>
                    <Radio value="video">Vídeo</Radio>
                  </Stack>
                </RadioGroup>
                {mediaTypeMobile === 'image' ? (
                  <>
                    <ImageCropInput
                      label="Imagem"
                      value={banner.image_mobile ?? null}
                      onChange={(file) => updateField('image_mobile', file)}
                      aspectRatio={390 / 413}
                      maxOutputSize={MOBILE_IMAGE_MAX_SIDE}
                      suggestedSize="390px x 413px"
                      previewAspectRatio="390 / 413"
                      isDisabled={!permissions.editThisBanner}
                      error={fieldError('image_mobile')}
                    />
                  </>
                ) : (
                  <>
                    <FormField label="URL do vídeo" error={fieldError('video_url_mobile')}>
                      <Input
                        placeholder="Insira a URL do vídeo"
                        value={banner.video_url_mobile}
                        onChange={(e) => updateField('video_url_mobile', e.target.value)}
                        onBlur={() => completeUrl('video_url_mobile')}
                        type="url"
                        color="gray.900"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="md"
                        px={4}
                        py={2}
                        _hover={{
                          borderColor: 'gray.500',
                          '&::placeholder': {
                            color: 'gray.700'
                          }
                        }}
                        _focus={{
                          borderColor: 'gray.600',
                          boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
                        }}
                        _placeholder={{ color: 'gray.500' }}
                        disabled={!permissions.editThisBanner}
                      />
                    </FormField>

                    {banner.video_url_mobile && (
                      <>
                        {isVimeoUrl(banner.video_url_mobile) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              src={`https://player.vimeo.com/video/${getVimeoVideoId(banner.video_url_mobile)}`}
                              title="Vídeo Preview"
                              width="100%"
                              height="200"
                              allow="autoplay; fullscreen"
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px'
                              }}
                            />
                          </Box>
                        )}
                        {isYouTubeUrl(banner.video_url_mobile) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              width="100%"
                              height="200"
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(banner.video_url_mobile)}`}
                              title="YouTube video player"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack spacing={4} justify="end">
          {permissions.editThisBanner && (
            <HStack>
              <IconButton
                icon={<ChevronUpIcon />}
                aria-label="Mover para cima"
                title="Mover para cima"
                colorScheme="gray"
                size="sm"
                onClick={() => changePosition('up')}
              />
              <IconButton
                icon={<ChevronDownIcon />}
                aria-label="Mover para baixo"
                title="Mover para baixo"
                colorScheme="gray"
                size="sm"
                onClick={() => changePosition('down')}
              />
            </HStack>
          )}
          <Switch
            title={banner.is_active ? 'Desativar Banner' : 'Ativar Banner'}
            isChecked={!!data.is_active}
            hidden={!isPersistedBanner(data.id)}
            onChange={(e) => handleSwitch(e, data.id)}
            isDisabled={!permissions.editThisBanner || isSwitchSaving(data.id)}
          />
          {permissions.deleteBanner && (
            <Button colorScheme="red" size="sm" onClick={openDeleteDialog}>
              Excluir
            </Button>
          )}
        </HStack>
      </Stack>
      <BannerPreview
        banner={banner}
        desktopMediaType={mediaType}
        mobileMediaType={mediaTypeMobile}
        isOpen={preview.isOpen}
        onClose={preview.onClose}
      />
    </Flex>
  )
}

export default BannerPageCard
